import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  GitlabClient,
  type GitlabProvisioningManageStatusResponse,
} from '../httpClients/GitlabClient';
import { QUERY_KEYS } from './consts';
import { getProvisionStatusRefetchInterval } from './useGitlabProvisionStatusQuery';

const RETRY_LIMIT = 3;

/**
 * Polls the v2 manage status until terminal (done/failed). Disabled until a
 * `manageId` exists; reuses the provision poll's terminal/interval logic (same
 * status enum). Polling stops automatically on a terminal status.
 * @param manageId - Id returned by the manage mutation; query disabled while undefined.
 * @param options - Optional TanStack Query overrides.
 * @returns TanStack Query result wrapping `GitlabProvisioningManageStatusResponse`.
 */
export const useGitlabProvisioningManageStatusQuery = (
  manageId: string | undefined,
  options: Partial<
    UseQueryOptions<
      GitlabProvisioningManageStatusResponse,
      Error,
      GitlabProvisioningManageStatusResponse
    >
  > = {},
) =>
  useQuery({
    queryKey: [QUERY_KEYS.GITLAB_MANAGE_STATUS, manageId],
    queryFn: () => GitlabClient.getProvisioningManageStatus(manageId as string),
    enabled: manageId !== undefined,
    // Stop polling once the query settles into an error state (which only happens
    // after its retries are exhausted): the Install step then surfaces a failure
    // + Retry (a fresh, idempotent run) instead of hammering the endpoint or
    // hanging on a loader.
    refetchInterval: (query) =>
      query.state.status === 'error'
        ? false
        : getProvisionStatusRefetchInterval(query.state.data?.status),
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) return false;

      return failureCount < RETRY_LIMIT;
    },
    ...options,
  });
